import {useUpmPlant} from '@hconnect/upm'
import {useForm} from 'react-hook-form'

import {useKilns, useRefraModal} from '@refra/modules/common/providers'
import type {Refractory} from '@refra/modules/refra-catalog/types'
import {useEditMaterialInstallation} from '@refra/modules/refra-planning/hooks/mutations/useEditMaterialInstallation'
import {
  EditMaterialInstallationForm,
  MaterialInstallationDetails
} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface useUpdateInstallationFormParams {
  materialInstallation: MaterialInstallationDetails
  assignedRefractory: Refractory
  shutdownId: number
}

export const useUpdateInstallationForm = ({
  materialInstallation,
  assignedRefractory,
  shutdownId
}: useUpdateInstallationFormParams) => {
  const plantCode = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId: plantCode})
  const {selectedKiln} = useKilns()
  const {closeModal} = useRefraModal()

  const {mutate: editMaterialInstallation, isPending: isEditingMaterialInstallation} =
    useEditMaterialInstallation()

  const initialBrickCalculations = {
    sections: materialInstallation?.brickCalculation?.sections ?? [],
    brickShape2: '',
    brickShape1: '',
    keyBricksCount: 2,
    useRefratechnikKeyBricks: false
  }

  const formMethods = useForm<EditMaterialInstallationForm>({
    defaultValues: {
      refractoryId: assignedRefractory.id,
      fromMeter: materialInstallation.fromMeter.toString(),
      toMeter: materialInstallation.toMeter.toString(),
      isMortarRequired: materialInstallation.isMortarRequired,
      brickCalculations: initialBrickCalculations
    }
  })

  const onSubmit = formMethods.handleSubmit((values: EditMaterialInstallationForm) => {
    if (values.refractoryId !== null) {
      const hasBrickCalculationChanges = !!formMethods.formState.dirtyFields.brickCalculations
      const material = {
        refractoryId: values.refractoryId,
        fromMeter: Number(values.fromMeter),
        toMeter: Number(values.toMeter),
        isMortarRequired: values.isMortarRequired,
        ...(hasBrickCalculationChanges && values.brickCalculations
          ? {brickCalculation: values.brickCalculations}
          : {})
      }

      editMaterialInstallation(
        {
          material,
          plantUpmId: upmPlant.upmId,
          kilnUpmId: selectedKiln.upmId,
          shutdownId: shutdownId,
          installationId: materialInstallation.id.toString()
        },
        {onSuccess: () => onClose()}
      )
    }
  })

  const onClose = () => {
    formMethods.reset()
    closeModal()
  }

  return {formMethods, isFormBeingProcessed: isEditingMaterialInstallation, onSubmit, onClose}
}
